import { useState } from 'react';
import styled from 'styled-components';
import SaveIcon from '../assets/save.png';
import PencilIcon from '../assets/colour.png';
import HandIcon from '../assets/hand.png';
import CustomButton from './Button';
import SaveActiveIcon from '../assets/save_active.png';
import PencilActiveIcon from '../assets/colour_active.png';
import { IconLayoutGridRemove, IconBucketDroplet } from '@tabler/icons-react';
import HandActiveIcon from '../assets/hand_active.png';
import { Box, Typography } from '@mui/material';
import CanvasPreview from './CanvasPreview';
import ToolsSave from './ToolsSave';
import ToolsPencil from './ToolsPencil';
import ToolsGrid from './ToolsGrid';
import ToolsFill from './ToolsFill';

type TOOLS_TYPES = 'pencil' | 'hand' | 'save' | 'fill' | 'grid';
const TOOLS: { [key in TOOLS_TYPES]: { icon: string | any; label: string; activeIcon: string | any } } = {
	pencil: { icon: PencilIcon, label: 'Pencil', activeIcon: PencilActiveIcon },
	hand: { icon: HandIcon, label: 'Hand', activeIcon: HandActiveIcon },
	grid: { icon: IconLayoutGridRemove, label: 'Grid', activeIcon: IconLayoutGridRemove },
	fill: { icon: IconBucketDroplet, label: 'Fill', activeIcon: IconBucketDroplet },
	save: { icon: SaveIcon, label: 'Save', activeIcon: SaveActiveIcon },
};

function Tools() {
	const [activeTool, setActiveTool] = useState<TOOLS_TYPES>('pencil');
	const [dragging, setDragging] = useState(false);
	const [preview, setPreview] = useState(false);

	const p5 = () => window.editSketch; // helper to get the p5 instance of editSketch

	const handleActiveTool = (tool: TOOLS_TYPES) => {
		const prevTool = activeTool;
		setActiveTool(tool);
		const sketch = p5();

		// Leave bucket mode when switching away from the Fill tool
		if (prevTool === 'fill' && tool !== 'fill') {
			sketch?.setBucketActive?.(false);
		}

		// Hand drag toggle (unchanged behaviour)
		if ((tool === 'hand' && !dragging) || (tool !== 'hand' && dragging)) {
			updateDragToggle();
		}

		// Enter bucket mode when selecting the Fill tool
		if (tool === 'fill') {
			sketch?.setBucketActive?.(true);
		}
	};

	const updateDragToggle = () => {
		p5()?.toggleMode();
		setDragging(s => !s);
	};

	// on preview open, toggle dragging mode so on backdrop click it wont draw
	const openPreview = () => {
		setPreview(true);
		if (activeTool !== 'hand') {
			updateDragToggle();
		}
	};

	// on preview close, toggle dragging mode back to drawing
	const closePreview = () => {
		setPreview(false);
		if (activeTool !== 'hand' && dragging) {
			updateDragToggle();
		}
	};

	return (
		<>
			<ToolsContainer>
				{activeTool === 'pencil' && <ToolsPencil p5={p5} />}

				{activeTool === 'fill' && <ToolsFill p5={p5} />}

				{activeTool === 'hand' && (
					<Box flex={1}>
						<Typography mx={2} mt={1} mb={0} component="h5" fontWeight={700} fontSize={12}>
							Move Canvas
						</Typography>
						<Typography mx={2} my={1} fontSize={12}>
							Move the canvas around by dragging it with your finger.
						</Typography>
					</Box>
				)}

				{activeTool === 'grid' && (
					<Box flex={1}>
						<Typography mx={2} mt={1} mb={0} component="h5" fontWeight={700} fontSize={12}>
							Grid Features
						</Typography>

						<Buttons style={{ margin: '10px 0 15px' }}>
							<ToolsGrid />
						</Buttons>
					</Box>
				)}

				{activeTool === 'save' && (
					<Box flex={1}>
						<Typography mx={2} mt={1} mb={0} component="h5" fontWeight={700} fontSize={12}>
							Save Image
						</Typography>

						<Buttons style={{ margin: '10px 0 15px' }}>
							<ToolsSave openPreview={openPreview} />
						</Buttons>
					</Box>
				)}
			</ToolsContainer>

			<Buttons>
				{Object.entries(TOOLS).map(([key, value]) => (
					<CustomButton
						key={key}
						imageSize={30}
						onClick={() => handleActiveTool(key as any)}
						image={key === activeTool ? value.activeIcon : value.icon}
						Icon={typeof value.icon === 'string' ? undefined : value.icon}
						active={key === activeTool}
					/>
				))}
			</Buttons>

			{/* MODALS */}
			{preview && <CanvasPreview open={preview} onClose={closePreview} />}
		</>
	);
}

export default Tools;

const ToolsContainer = styled.div`
	background-color: #222;
	border-radius: 15px;
	padding: 5px 20px;
	margin: 15px 0;
`;

const Buttons = styled.div`
	display: flex;
	flew-direction: row;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	padding: 0 15px 15px;
`;
